// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a specialized map marker component for displaying location information
 * on a Google Maps instance. It visualizes asset counts and handles user interactions.
 */
import { MapMarker } from '@/components/map-marker';
import { Location } from '@/location/api/types';
import { SparseLocation } from '@/sparse/api/types';
import { formatNumber } from '@/utilities/number';
import { Typography } from '@mui/material';

/**
 * Props for the LocationMapMarker component.
 */
interface Props {
  /**
   * The location to render on the map.
   * Can be either a full Location or SparseLocation object.
   * If a full Location object is provided, it will display the asset count.
   */
  location: Location | SparseLocation;

  /**
   * Callback fired when the marker is clicked.
   * Typically used to open a drawer or modal with detailed location information.
   */
  onClick: () => void;
}

/**
 * A marker component that displays location information on a Google Maps instance.
 *
 * Features:
 * - Displays asset count for locations (if available)
 * - Uses different colors for customer vs. non-customer locations
 * - Handles click interactions for opening detailed views
 * - Automatically converts string coordinates to numbers
 * - Provides visual contrast with background color
 * - Filters out locations assigned to supplier-only companies
 *
 * Visual Styling:
 * - Customer locations use secondary color
 * - Non-customer locations use primary color
 * - Text color automatically adjusts for contrast
 * - Fixed size of 2rem for consistency
 *
 * Filtering Behavior:
 * - Only shows locations where customer is null OR customer.isCustomer is true
 * - Hides locations assigned to supplier-only companies
 *
 * @example
 * ```tsx
 * // Basic usage
 * <LocationMapMarker
 *   location={locationData}
 *   onClick={() => openLocationDetails(locationData.id)}
 * />
 * ```
 */
export function LocationMapMarker(props: Props) {
  // Filter out locations assigned to supplier-only companies
  // Only show if location has no customer or if customer has isCustomer role
  if (
    props.location.customer &&
    !props.location.customer.isCustomer &&
    'assetCount' in props.location &&
    props.location.assetCount === 0
  ) {
    return null;
  }

  // Determine background color based on whether location belongs to a customer
  const backgroundColor = props.location.customer
    ? 'secondary.main'
    : 'primary.main';

  // Set contrasting text color based on background
  const color = props.location.customer
    ? 'secondary.contrastText'
    : 'primary.contrastText';

  return (
    <MapMarker
      size="2rem"
      color={backgroundColor}
      latitude={Number(props.location.latitude)}
      longitude={Number(props.location.longitude)}
      onClick={props.onClick}
    >
      {/* Only display asset count if it's available (full Location object) */}
      {'assetCount' in props.location ? (
        <Typography variant="body1" fontWeight="500" color={color}>
          {formatNumber(props.location.assetCount, {
            abbreviate: true,
            digits: 0,
          })}
        </Typography>
      ) : null}
    </MapMarker>
  );
}
